import { useCallback, useMemo, useState } from 'react';
import { useForm as useRHFForm, UseFormReturn } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { checkEmptyValues } from '../../../core/formValidation';
import { getApiStatus } from '../../../core/utils';
import { statusSelector } from '../../../store/authReducer';
import {
  checkTokenStatus,
  PASSWORD_RESET_REQUEST,
  setPassword,
  statusError,
  statusReset,
} from '../actions';

export interface FormValues {
  password: string;
  confirmPassword: string;
}

// Minimal Formik-like facade so the component API remains unchanged
type FormikFacade = {
  getFieldProps: (name: keyof FormValues | string) => {
    name: string;
    value: any;
    onChange: (e: any) => void;
    onBlur: (e: any) => void;
  };
  isSubmitting: boolean;
  isValid: boolean;
};

interface Form {
  formik: FormikFacade;
  handleSubmitClick: () => void;
  statusMessage: { type: string; message: string };
  removeStatusMessage: () => void;
  setErrorMessage: (message: string) => void;
  checkValidationToken: (token: string) => void;
}

const useSetPasswordForm = (): Form => {
  const [validToken, setToken] = useState('');
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => getApiStatus(state, PASSWORD_RESET_REQUEST)) as boolean;
  const statusMessage = useSelector((state) => statusSelector(state));

  const removeStatusMessage = () => {
    dispatch(statusReset());
  };

  const setErrorMessage = (message: string) => {
    dispatch(statusError(message));
  };

  const checkValidationToken = (token: any) => {
    dispatch(checkTokenStatus(token));
    setToken(token);
  };

  const rhf: UseFormReturn<FormValues> = useRHFForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = useCallback(
    (values: FormValues) => {
      if (statusMessage && statusMessage.message !== 'Token is invalid') {
        dispatch(setPassword({ token: validToken, password: values.confirmPassword }));
      }
    },
    [dispatch, statusMessage, validToken]
  );

  // Watch form values to trigger re-renders when they change
  const watchedValues = rhf.watch();

  const formik: FormikFacade = useMemo(() => {
    const getFieldProps = (name: keyof FormValues | string) => {
      const value = (rhf.getValues() as any)[name];
      const onChange = (e: any) => {
        const next = e?.target?.value ?? e;
        rhf.setValue(name as any, next, { shouldValidate: true, shouldDirty: true });
      };
      const onBlur = () => rhf.trigger(name as any);
      return { name: String(name), value, onChange, onBlur };
    };

    const vals = rhf.getValues();
    const errors = checkEmptyValues(vals as any) as Record<string, any>;

    return {
      getFieldProps,
      isSubmitting: rhf.formState.isSubmitting || !!isLoading,
      isValid: Object.keys(errors || {}).length === 0,
    };
  }, [rhf, isLoading, watchedValues]);

  const handleSubmitClick = useCallback(() => {
    rhf.handleSubmit(onSubmit)();
  }, [rhf, onSubmit]);

  return {
    formik,
    handleSubmitClick,
    statusMessage,
    removeStatusMessage,
    setErrorMessage,
    checkValidationToken,
  };
};

export default useSetPasswordForm;
